import { useState } from 'react';
import { SelectField, TextField } from './Form';
import type { LocalAccountDto } from '../lib/types';

/**
 * Shared with every screen that codes a line or a default setting to a QuickBooks account, so
 * "search this company's chart, then pick from what matched" behaves identically everywhere
 * instead of one screen growing a free-text id box that only works if you already know it.
 */
export type AccountLookupRole = 'expense' | 'ap' | 'payment';

function compactAccountType(value: string | null): string {
  return (value ?? '').replace(/\s+/g, '').toLowerCase();
}

export function accountMatchesRole(account: LocalAccountDto, role: AccountLookupRole): boolean {
  if (!account.active || account.missingSinceUtc !== null || !account.isEligibleForNewPosting) return false;

  const type = compactAccountType(account.accountType);
  if (role === 'ap') return type === 'accountspayable';
  if (role === 'payment') return type === 'bank' || type === 'creditcard';

  return type !== 'accountspayable'
    && type !== 'accountsreceivable'
    && type !== 'bank'
    && type !== 'creditcard';
}

export function accountOptionLabel(account: LocalAccountDto): string {
  const number = account.accountNumber ? `${account.accountNumber} · ` : '';
  const type = account.accountType ? ` — ${account.accountType}` : '';
  return `${number}${account.name}${type}`;
}

export interface AccountLookupFieldProps {
  label: string;
  searchLabel: string;
  role: AccountLookupRole;
  accounts: LocalAccountDto[];
  loading: boolean;
  selectedId: string | null;
  selectedName: string | null;
  disabled?: boolean;
  onChange: (id: string | null, name: string | null) => void;
}

export function AccountLookupField({
  label,
  searchLabel,
  role,
  accounts: allAccounts,
  loading,
  selectedId,
  selectedName,
  disabled,
  onChange,
}: AccountLookupFieldProps): JSX.Element {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const matchingAccounts = allAccounts
    .filter((account) => accountMatchesRole(account, role))
    .filter((account) => {
      if (!term) return true;
      return [account.accountNumber, account.name, account.fullyQualifiedName, account.accountType]
        .some((value) => (value ?? '').toLowerCase().includes(term));
    });

  // A large chart stays usable: search narrows the complete set, while the select never renders
  // thousands of DOM options at once. The complete chart was already read once above.
  const accounts = matchingAccounts.slice(0, 100);
  const options = [
    { value: '', label: 'Not configured' },
    ...accounts.map((account) => ({ value: account.externalAccountId, label: accountOptionLabel(account) })),
  ];

  if (selectedId && !options.some((option) => option.value === selectedId)) {
    options.splice(1, 0, {
      value: selectedId,
      label: `${selectedName ?? 'Current saved account'} — current selection`,
    });
  }

  const hint = loading
    ? "Loading the connected company's Chart of Accounts…"
    : matchingAccounts.length > 100
      ? `${matchingAccounts.length} accounts match. Type more to narrow the list; the first 100 are shown.`
      : 'Type part of an account number or name to narrow the QuickBooks lookup.';

  return (
    <>
      <TextField
        label={searchLabel}
        value={search}
        onChange={setSearch}
        placeholder="Search account number or name"
        disabled={disabled}
      />
      <SelectField
        label={label}
        value={selectedId ?? ''}
        disabled={disabled}
        onChange={(value) => {
          if (!value) {
            onChange(null, null);
            return;
          }

          const account = allAccounts.find((candidate) => candidate.externalAccountId === value);
          onChange(value, account?.name ?? selectedName);
        }}
        options={options}
        hint={hint}
      />
    </>
  );
}
